if (!customElements.get('retailer-finder')) {
  customElements.define(
    'retailer-finder',
    class RetailerFinder extends HTMLElement {
      connectedCallback() {
        this.searchInput = this.querySelector('[data-retailer-search]');
        this.cards = Array.from(this.querySelectorAll('[data-retailer-card]'));
        this.count = this.querySelector('[data-retailer-count]');
        this.countLabel = this.querySelector('[data-retailer-count-label]');
        this.emptyState = this.querySelector('[data-retailer-empty]');
        this.clearButton = this.querySelector('[data-retailer-clear]');
        this.map = this.querySelector('[data-retailer-map]');
        this.mapLabel = this.querySelector('[data-retailer-map-label]');
        this.list = this.querySelector('[data-retailer-list]');
        this.status = this.querySelector('[data-retailer-status]');
        this.mapContainer = this.querySelector('.carson-retailer-finder__map');
        this.searchVersion = 0;
        this.cards.forEach((card) => {
          card.postcode = card.dataset.retailerPostcode?.trim() ||
            card.dataset.retailerAddress?.match(/\b\d{4}\b(?![\s\S]*\b\d{4}\b)/)?.[0] || '';
        });

        this.onSearch = this.onSearch.bind(this);
        this.onCardClick = this.onCardClick.bind(this);
        this.onCardKeydown = this.onCardKeydown.bind(this);
        this.onClear = this.onClear.bind(this);

        this.searchInput?.addEventListener('input', this.onSearch);
        this.clearButton?.addEventListener('click', this.onClear);
        this.cards.forEach((card) => {
          card.addEventListener('click', this.onCardClick);
          card.addEventListener('keydown', this.onCardKeydown);
        });

        const defaultCard = this.cards.find((card) => card.dataset.defaultSelected === 'true') || this.cards[0];
        if (defaultCard) this.selectCard(defaultCard);
      }

      disconnectedCallback() {
        this.searchVersion++;
        this.searchInput?.removeEventListener('input', this.onSearch);
        this.clearButton?.removeEventListener('click', this.onClear);
        this.cards?.forEach((card) => {
          card.removeEventListener('click', this.onCardClick);
          card.removeEventListener('keydown', this.onCardKeydown);
        });
      }

      async onSearch(event) {
        const query = event.target.value.trim().toLocaleLowerCase();
        const version = ++this.searchVersion;
        const isPostcode = /^\d{4}$/.test(query);
        const visibleCards = this.cards.filter((card) => !query || (isPostcode
          ? card.postcode === query
          : card.dataset.retailerSearchText.includes(query)));

        // Restore editor order and remove distances from the previous search.
        this.cards.forEach((card) => {
          this.list.insertBefore(card, this.emptyState);
          const distance = card.querySelector('[data-retailer-distance]');
          if (distance) distance.hidden = true;
        });
        this.setStatus('');
        this.showResults(visibleCards);
        if (visibleCards.length || !isPostcode) return;

        this.setStatus('Finding nearby retailers…');
        if (this.emptyState) this.emptyState.hidden = true;
        try {
          const postcodes = await this.loadPostcodes();
          if (version !== this.searchVersion) return;
          const origin = postcodes[query];
          if (!origin) {
            this.setStatus(Object.prototype.hasOwnProperty.call(postcodes, query)
              ? 'Location unavailable for this postcode. Try a nearby postcode or suburb.'
              : 'Postcode not recognised. Enter a valid four-digit Australian postcode.');
            this.showResults([]);
            return;
          }
          const nearest = this.cards
            .filter((card) => /^(australia|au|aus)$/i.test(card.dataset.retailerCountry?.trim() || 'Australia') && postcodes[card.postcode])
            .map((card) => ({ card, distance: this.distanceKm(origin, postcodes[card.postcode]) }))
            .sort((a, b) => a.distance - b.distance)
            .slice(0, 5);
          nearest.forEach(({ card, distance }) => {
            this.list.insertBefore(card, this.emptyState);
            const label = card.querySelector('[data-retailer-distance]');
            if (label) {
              label.textContent = `Approx. ${distance < 1 ? '<1' : Math.round(distance).toLocaleString('en-AU')} km away`;
              label.hidden = false;
            }
          });
          this.setStatus(nearest.length
            ? `No retailers in ${query}. Showing the nearest ${nearest.length === 1 ? 'retailer' : `${nearest.length} retailers`}. Distances are approximate, measured in a straight line between postcode areas.`
            : 'No nearby retailer locations available. Try a different suburb or retailer name.');
          this.showResults(nearest.map(({ card }) => card), true);
        } catch {
          if (version !== this.searchVersion) return;
          this.setStatus('Nearby search is temporarily unavailable. Try again or search by suburb or retailer name.');
          this.showResults([]);
        }
      }

      loadPostcodes() {
        if (!this.postcodesPromise) {
          this.postcodesPromise = fetch(this.dataset.postcodesUrl)
            .then((response) => {
              if (!response.ok) throw new Error('Postcode data unavailable');
              return response.json();
            })
            .catch((error) => {
              this.postcodesPromise = null;
              throw error;
            });
        }
        return this.postcodesPromise;
      }

      distanceKm([lat1, lon1], [lat2, lon2]) {
        const radians = Math.PI / 180;
        const a = Math.sin((lat2 - lat1) * radians / 2) ** 2 +
          Math.cos(lat1 * radians) * Math.cos(lat2 * radians) * Math.sin((lon2 - lon1) * radians / 2) ** 2;
        return 6371 * 2 * Math.asin(Math.sqrt(Math.min(1, Math.max(0, a))));
      }

      setStatus(message) {
        if (!this.status) return;
        this.status.textContent = message;
        this.status.hidden = !message;
      }

      showResults(visibleCards, selectFirst = false) {
        this.cards.forEach((card) => { card.hidden = !visibleCards.includes(card); });
        this.updateCount(visibleCards.length);
        if (this.emptyState) this.emptyState.hidden = visibleCards.length > 0;
        if (this.mapContainer) this.mapContainer.hidden = visibleCards.length === 0;

        const activeCard = this.cards.find((card) => card.classList.contains('is-active'));
        if (visibleCards.length && (selectFirst || !activeCard || activeCard.hidden)) {
          this.selectCard(visibleCards[0]);
        }
      }

      onCardClick(event) {
        const card = event.currentTarget;
        if (event.target.closest('a, button')) return;
        this.selectCard(card);
      }

      onCardKeydown(event) {
        if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        this.selectCard(event.currentTarget);
      }

      onClear() {
        if (!this.searchInput) return;
        this.searchInput.value = '';
        this.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.searchInput.focus();
      }

      updateCount(count) {
        if (this.count) this.count.textContent = count;
        if (this.countLabel) this.countLabel.textContent = count === 1 ? 'location' : 'locations';
      }

      selectCard(card) {
        if (!card) return;

        this.cards.forEach((item) => item.classList.toggle('is-active', item === card));

        const name = card.dataset.retailerName;
        const query = card.dataset.retailerMapQuery;
        if (this.map && query) {
          this.map.src = `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=15&output=embed`;
          this.map.title = `Map for ${name}`;
        }
        if (this.mapLabel) this.mapLabel.textContent = name;
      }
    },
  );
}
